// Reports (docs/plans/phase1-reports.md §8, §9). Filters go in the request body, never
// the address, because a register filter can identify a patient. Downloads are fetched as
// files; the server audits each export before sending it.
import { queryOptions } from '@tanstack/react-query';
import {
  collectionsReportSchema,
  daySummarySchema,
  labRegisterSchema,
  opdRegisterSchema,
  pharmacyRegisterSchema,
  reportFacilitiesSchema,
  revenueReportSchema,
  unclaimedReportSchema,
  type ReportKey,
  type ReportPeriod,
} from '@wodi/shared';

import { api, downloadFile } from './client.ts';

export const reportKeys = {
  all: ['reports'] as const,
  facilities: ['reports', 'facilities'] as const,
  report: (key: ReportKey, query: unknown) => ['reports', key, query] as const,
};

export const reportFacilitiesQuery = queryOptions({
  queryKey: reportKeys.facilities,
  queryFn: () => api('GET', '/reports/facilities', { schema: reportFacilitiesSchema }),
  staleTime: 60_000,
});

export interface MoneyQuery {
  period: ReportPeriod;
  facilityIds?: string[] | undefined;
}

export const fetchCollections = (body: MoneyQuery) =>
  api('POST', '/reports/daily-collections', { body, schema: collectionsReportSchema });

export const fetchRevenue = (body: MoneyQuery) =>
  api('POST', '/reports/revenue', { body, schema: revenueReportSchema });

export const fetchUnclaimed = (body: { facilityIds?: string[] | undefined }) =>
  api('POST', '/reports/payer-share-unclaimed', { body, schema: unclaimedReportSchema });

export const fetchDaySummary = (body: { day: string; facilityIds?: string[] | undefined }) =>
  api('POST', '/reports/day-summary', { body, schema: daySummarySchema });

export interface OpdQuery {
  period: ReportPeriod;
  servicePointId?: string;
  payerId?: string;
  visitType?: 'outpatient' | 'walk_in';
  diagnosisCode?: string;
}

export const fetchOpdRegister = (body: OpdQuery & { cursor?: string; limit?: number }) =>
  api('POST', '/reports/opd-register', { body, schema: opdRegisterSchema });

export interface LabQuery {
  period: ReportPeriod;
  sectionId?: string;
}

export const fetchLabRegister = (body: LabQuery & { cursor?: string; limit?: number }) =>
  api('POST', '/reports/lab-register', { body, schema: labRegisterSchema });

export interface PharmacyQuery {
  period: ReportPeriod;
  kind?: 'dispense' | 'sale';
}

export const fetchPharmacyRegister = (body: PharmacyQuery & { cursor?: string; limit?: number }) =>
  api('POST', '/reports/pharmacy-register', { body, schema: pharmacyRegisterSchema });

/** Records a print (the server audits it); call before opening the print dialog. */
export const reportPrinted = (
  key: ReportKey,
  body: { period: ReportPeriod; facilityIds: string[]; rowCount: number },
) => api('POST', `/reports/${key}/printed`, { body });

/**
 * Downloads a report as CSV. The file name comes from the server, which builds it from the
 * report, facility code and period only.
 */
export function downloadReport(key: ReportKey, body: object): Promise<string> {
  return downloadFile(
    `/api/v1/reports/${key}/export`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'text/csv' },
      body: JSON.stringify(body),
    },
    `wodi_${key}.csv`,
  );
}
